import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Loader2, Plus, Pencil, Trash2, RefreshCw, ChevronDown, ChevronUp,
  ChevronRight, Check, X, GitMerge, Scissors, ArrowUpCircle, ArrowDownCircle, GripVertical,
  Sparkles, History, Plus as PlusIcon, Minus, MoveRight, AlertTriangle,
} from 'lucide-react';
import API_BASE_URL from '../Config/config';

/**
 * EliteRoadmapEditor — Phase B (24-Aug-2026) roadmap review/edit screen
 * (spec §6-9). Opened from EliteTechnologyManager once a technology has an
 * Elite Mastery Blueprint and/or a Roadmap. Every action here calls a real
 * backend endpoint (technologyRoadmapService.js) and re-renders from
 * whatever it actually persisted — there is no local-only edit state that
 * isn't backed by the database, and no raw JSON editing anywhere on this
 * screen: every field is a real input bound to a real column.
 *
 * generateRoadmap on the backend now requires a Blueprint to exist first
 * (spec §5-6 ordering), so this screen surfaces "Generate Blueprint" before
 * "Generate Roadmap" ever becomes clickable — matching, not working around,
 * that server-side gate.
 */

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'user-id': getUserId() || '',
});

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders(), ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
};

const jsonArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const parsed = JSON.parse(v); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
};

const DIFFICULTY_OPTIONS = ['foundation', 'intermediate', 'advanced', 'expert'];
const LEARNING_MODE_OPTIONS = ['reading', 'hands_on', 'video', 'interactive', 'project'];

const DIFFICULTY_TONES = {
  foundation: 'bg-emerald-50 text-emerald-700',
  intermediate: 'bg-blue-50 text-blue-700',
  advanced: 'bg-amber-50 text-amber-700',
  expert: 'bg-red-50 text-red-600',
};

const ScoreBar = ({ label, value, hint }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = pct >= 80 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div title={hint}>
      <div className="flex items-center justify-between text-[11px] text-gray-700 mb-1">
        <span>{label}</span>
        <span className="font-bold text-gray-700">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone }} />
      </div>
    </div>
  );
};

/** A comma-separated tag list bound to a real array field — the closest
 * thing to "editing prerequisites" this screen offers, deliberately not a
 * raw JSON textarea. Titles are matched against sibling topic titles by the
 * backend's computeRoadmapQualityScores, so free text here is fine — an
 * unmatched entry just shows as a broken reference in Dependency
 * Integrity/Continuity, which is accurate, not swallowed. */
const TagListInput = ({ value, onChange, placeholder }) => {
  const [text, setText] = useState((jsonArray(value) || []).join(', '));
  useEffect(() => { setText((jsonArray(value) || []).join(', ')); }, [value]);
  return (
    <input
      type="text"
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(text.split(',').map((s) => s.trim()).filter(Boolean))}
      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
    />
  );
};

const Field = ({ label, children }) => (
  <div className="mb-2">
    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-0.5">{label}</label>
    {children}
  </div>
);

/**
 * Icon action with a real hover tooltip (26-Aug-2026).
 *
 * The row of icons on every section and topic was unlabelled and drawn in
 * text-gray-300, so an admin had to click one to find out what it did — and on
 * a normal monitor could barely see them at all. This gives every icon a
 * readable near-black default, a colour-coded hover state, and a tooltip that
 * says what the action does rather than just naming it.
 *
 * The tooltip is CSS-only (group-hover) so it costs no state and no library,
 * and is anchored to the button's right edge so it opens inwards instead of
 * spilling out of the card. `aria-label` carries the same text for screen
 * readers and keyboard users get it on focus.
 */
const IconAction = ({ tip, onClick, disabled, tone = 'hover:text-gray-900', size = 14, children, ...rest }) => (
  <span className="relative inline-flex group">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={tip}
      className={`text-gray-700 ${tone} disabled:opacity-30 disabled:hover:text-gray-700 transition-colors`}
      {...rest}
    >
      {typeof children === 'function' ? children(size) : children}
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full right-0 mb-1.5 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] font-semibold text-white shadow-lg opacity-0 translate-y-0.5 transition-all duration-100 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0"
    >
      {tip}
    </span>
  </span>
);

const EliteRoadmapEditor = ({ slug, onBack, onTechnologyChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // 25-Aug-2026: roadmap generation is async on the backend (POST returns
  // 202 and the Lambda works in the background — it can't finish inside API
  // Gateway's 29s window). While it runs we poll GET .../roadmap, which now
  // also returns `technology` (generation_status) and `generationJob` (the
  // real error text if it fails).
  const [generationJob, setGenerationJob] = useState(null);
  const [roadmapVersion, setRoadmapVersion] = useState(null);
  const [generatingSince, setGeneratingSince] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const pollRef = useRef(null);
  const [technology, setTechnology] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [blueprintOpen, setBlueprintOpen] = useState(false);
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState(new Set());
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeTitle, setMergeTitle] = useState('');
  const [splitTopicId, setSplitTopicId] = useState(null);
  const [splitTitles, setSplitTitles] = useState('');
  const [addingSectionTitle, setAddingSectionTitle] = useState('');
  const [addingTopicFor, setAddingTopicFor] = useState(null);
  const [addingTopicTitle, setAddingTopicTitle] = useState('');
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  // 26-Aug-2026: drag a topic into any section (spec §8). `dragTopic` is the
  // topic being dragged; `dropAt` is the insertion point currently under the
  // pointer, as an index into the DISPLAYED list of that section.
  const [dragTopic, setDragTopic] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  // 26-Aug-2026: Re-generate RoadMap from admin feedback. `regenOpen` drives
  // the modal; `lastDiff` is the data-derived change summary shown afterwards.
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenFields, setRegenFields] = useState({ missingSections: '', missingTopics: '', topicsToExpand: '', topicsToRemove: '', topicsToReorganize: '' });
  const [regenMode, setRegenMode] = useState('improve');
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [lastDiff, setLastDiff] = useState(null);
  const [showDiffDetail, setShowDiffDetail] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState([]);
  // Lets the admin clear the failure notice without it reappearing on the next poll.
  const [regenDismissed, setRegenDismissed] = useState(false);
  const regenInFlight = useRef(false);

  // 25-Aug-2026 fix: `onTechnologyChange` is passed as an inline arrow from
  // EliteTechnologyManager, so its identity changes on every parent render.
  // Having it in `load`'s dependency list created an infinite loop —
  // load() → onTechnologyChange() → parent setState → parent re-render →
  // new onTechnologyChange → new load → useEffect([load]) → load() … — which
  // re-fetched /blueprint + /roadmap forever and left the spinner on screen
  // even though every request returned 200. Keep the latest callback in a ref
  // so `load` (and therefore the mount effect) only depends on `slug`.
  const onTechnologyChangeRef = useRef(onTechnologyChange);
  useEffect(() => { onTechnologyChangeRef.current = onTechnologyChange; }, [onTechnologyChange]);

  // `silent` skips the full-screen spinner — used by the generation poller so
  // the "Generating roadmap…" card doesn't flicker on every refresh.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [bpData, rmData] = await Promise.all([
        api(`/api/admin/elite-technologies/${slug}/blueprint`),
        api(`/api/admin/elite-technologies/${slug}/roadmap`),
      ]);
      // GET .../roadmap's `technology` is the fresher of the two (it's what
      // the poller reads), so prefer it when present.
      const tech = rmData.technology || bpData.technology;
      setTechnology(tech);
      setBlueprint(bpData.blueprint);
      setRoadmap(rmData);
      setGenerationJob(rmData.generationJob || null);
      setRoadmapVersion(rmData.roadmapVersion || null);
      if (onTechnologyChangeRef.current) onTechnologyChangeRef.current(tech);
    } catch (err) {
      setError(err.message || 'Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const isGenerating = technology?.generation_status === 'ROADMAP_GENERATING';

  // Poll while a roadmap generation is in flight — including after a page
  // refresh, since generation_status is persisted server-side. Stops itself
  // as soon as the status leaves ROADMAP_GENERATING; load() then renders
  // whatever the worker actually persisted.
  useEffect(() => {
    if (!isGenerating) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setGeneratingSince(null);
      return undefined;
    }
    // Prefer the job's real start time (survives page reloads) over "when
    // this tab started watching".
    const startedAt = generationJob?.started_at || generationJob?.created_at;
    setGeneratingSince((prev) => prev || (startedAt ? new Date(startedAt).getTime() : Date.now()));
    let cancelled = false;
    const tick = async () => {
      try {
        const rm = await api(`/api/admin/elite-technologies/${slug}/roadmap`);
        if (cancelled) return;
        setGenerationJob(rm.generationJob || null);
        if (rm.technology && rm.technology.generation_status !== 'ROADMAP_GENERATING') {
          await load({ silent: true });
        }
      } catch {
        // transient poll failure — keep polling; the next tick will retry
      }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => { cancelled = true; if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating, slug, load]);

  // Past this point a run is clearly overdue (the worker's whole AI budget is
  // 8 min; the server auto-heals an orphaned status on every poll). Offer the
  // admin an explicit way out rather than an endless spinner.
  const OVERDUE_AFTER_SEC = 5 * 60;
  const isOverdue = isGenerating && elapsedSec >= OVERDUE_AFTER_SEC;

  const handleResetAndRetry = () => {
    if (!window.confirm('Abandon the current roadmap generation and start a new one? Only do this if it looks stuck.')) return;
    run(async () => {
      const data = await api(`/api/admin/elite-technologies/${slug}/roadmap`, {
        method: 'POST', body: JSON.stringify({ force: true, reset: true }),
      });
      // A new run starts now — restart the elapsed clock (polling itself
      // keeps going, since the status never left ROADMAP_GENERATING).
      setGeneratingSince(Date.now());
      if (data && data.generationJob) setGenerationJob(data.generationJob);
    });
  };

  // Elapsed-time display for the "generating" card.
  useEffect(() => {
    if (!generatingSince) { setElapsedSec(0); return undefined; }
    const id = setInterval(() => setElapsedSec(Math.floor((Date.now() - generatingSince) / 1000)), 1000);
    return () => clearInterval(id);
  }, [generatingSince]);

  /** Wraps a mutating call: shows a busy state, surfaces errors via alert
   * (matching the rest of the admin console's convention), and always
   * re-loads from the server afterward so the screen only ever shows real
   * persisted state — never an optimistic guess. */
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      window.alert(err.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateBlueprint = () => run(() =>
    api(`/api/admin/elite-technologies/${slug}/blueprint`, { method: 'POST' })
  );

  const handleGenerateRoadmap = () => run(async () => {
    const force = technology?.generation_status === 'FAILED';
    const data = await api(`/api/admin/elite-technologies/${slug}/roadmap`, {
      method: 'POST', body: JSON.stringify({ force }),
    });
    // 202: the backend accepted the job; the poller (keyed off
    // generation_status === ROADMAP_GENERATING, which run()'s load() will
    // pick up) takes over from here. 200: it ran inline and is already done.
    if (data && data.generationJob) setGenerationJob(data.generationJob);
  });

  const handleApprove = () => run(() =>
    api(`/api/admin/elite-technologies/${slug}/roadmap/approve`, { method: 'POST' })
  );

  const openRegen = () => { setRegenError(null); setLastDiff(null); setShowDiffDetail(false); setRegenOpen(true); };
  const closeRegen = () => {
    // Cancel must not fire any request and must leave the roadmap untouched.
    if (regenBusy) return;
    setRegenOpen(false); setRegenError(null);
    setRegenFeedback(''); setRegenMode('improve');
    setRegenFields({ missingSections: '', missingTopics: '', topicsToExpand: '', topicsToRemove: '', topicsToReorganize: '' });
  };

  const splitList = (v) => String(v || '').split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
  const regenHasInput = regenFeedback.trim().length > 0 || Object.values(regenFields).some((v) => splitList(v).length > 0);

  const handleRegenerate = async () => {
    // Guard against double-submit at the source, not just via `disabled`.
    if (regenInFlight.current || !regenHasInput) return;
    regenInFlight.current = true;
    // A new attempt supersedes whatever the last one reported.
    setRegenDismissed(false); setLastDiff(null);
    setRegenBusy(true); setRegenError(null);
    try {
      const data = await api(`/api/admin/elite-technologies/${slug}/roadmap/regenerate`, {
        method: 'POST',
        body: JSON.stringify({
          feedback: regenFeedback.trim(),
          regenerationMode: regenMode,
          structuredFeedback: Object.fromEntries(Object.entries(regenFields).map(([k, v]) => [k, splitList(v)])),
        }),
      });
      setRegenOpen(false);
      setRegenFeedback('');
      setRegenFields({ missingSections: '', missingTopics: '', topicsToExpand: '', topicsToRemove: '', topicsToReorganize: '' });
      // 202 => a worker is running and the poller takes over. 200 => it ran
      // inline and the diff is already here.
      if (data && data.diff) setLastDiff(data.diff);
      await load({ silent: true });
    } catch (err) {
      setRegenError(err.message || 'RoadMap regeneration failed.');
    } finally {
      regenInFlight.current = false;
      setRegenBusy(false);
    }
  };

  const loadVersions = async () => {
    try {
      const data = await api(`/api/admin/elite-technologies/${slug}/roadmap/versions`);
      setVersions(data.versions || []);
      setFailedAttempts(data.failedAttempts || []);
      setShowVersions(true);
    } catch (err) {
      window.alert(err.message || 'Failed to load roadmap versions');
    }
  };

  // 26-Aug-2026: a regeneration that fails validation deliberately leaves the
  // roadmap untouched and puts the status back to ROADMAP_DRAFT — nothing was
  // lost, so FAILED would be a lie. But that made the failure invisible: the
  // page looked identical, no new version appeared, and the previous run's
  // success banner was still on screen. Surface it here instead.
  const lastRegenFailed = !isGenerating
    && generationJob?.status === 'FAILED'
    && !!generationJob?.error
    && (roadmap?.totalTopics || 0) > 0
    && !regenDismissed;
  const lastAttemptWasRegen = !!(generationJob?.payload?.regenerate);

  const handleAddSection = () => {
    if (!addingSectionTitle.trim()) return;
    run(async () => {
      await api(`/api/admin/elite-technologies/${slug}/sections`, {
        method: 'POST', body: JSON.stringify({ title: addingSectionTitle.trim() }),
      });
      setAddingSectionTitle('');
    });
  };

  const handleRenameSection = (sectionId) => {
    if (!renameValue.trim()) return;
    run(async () => {
      await api(`/api/admin/elite-technologies/${slug}/sections/${sectionId}`, {
        method: 'PUT', body: JSON.stringify({ title: renameValue.trim() }),
      });
      setRenamingSectionId(null);
    });
  };

  const handleRemoveSection = (section) => {
    const hasTopics = (section.topics || []).length > 0;
    const msg = hasTopics
      ? `Remove section "${section.title}" and its ${section.topics.length} topic(s)? This cannot be undone.`
      : `Remove empty section "${section.title}"?`;
    if (!window.confirm(msg)) return;
    run(() => api(`/api/admin/elite-technologies/${slug}/sections/${section.id}?force=true`, { method: 'DELETE' }));
  };

  const handleRegenerateSection = (sectionId) => {
    if (!window.confirm('Regenerate this section? Its current topics will be replaced.')) return;
    run(() => api(`/api/admin/elite-technologies/${slug}/sections/${sectionId}/regenerate`, { method: 'POST' }));
  };

  const handleMoveSection = (sectionId, direction) => {
    const ids = (roadmap?.sections || []).map((s) => s.id);
    const idx = ids.indexOf(sectionId);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    run(() => api(`/api/admin/elite-technologies/${slug}/sections/reorder`, {
      method: 'POST', body: JSON.stringify({ orderedSectionIds: ids }),
    }));
  };

  const handleAddTopic = (sectionId) => {
    if (!addingTopicTitle.trim()) return;
    run(async () => {
      await api(`/api/admin/elite-technologies/${slug}/sections/${sectionId}/topics`, {
        method: 'POST', body: JSON.stringify({ title: addingTopicTitle.trim() }),
      });
      setAddingTopicTitle('');
      setAddingTopicFor(null);
    });
  };

  const patchTopic = (topicId, fields) => run(() =>
    api(`/api/admin/elite-technologies/${slug}/topics/${topicId}`, {
      method: 'PUT', body: JSON.stringify(fields),
    })
  );

  const handleRemoveTopic = (topic) => {
    if (!window.confirm(`Remove topic "${topic.title}"?`)) return;
    run(() => api(`/api/admin/elite-technologies/${slug}/topics/${topic.id}`, { method: 'DELETE' }));
  };

  const handleRegenerateTopic = (topicId) => {
    run(() => api(`/api/admin/elite-technologies/${slug}/topics/${topicId}/regenerate`, { method: 'POST' }));
  };

  const handleMoveTopic = (sectionId, topicId, direction) => {
    const section = roadmap.sections.find((s) => s.id === sectionId);
    const ids = section.topics.map((t) => t.id);
    const idx = ids.indexOf(topicId);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    run(() => api(`/api/admin/elite-technologies/${slug}/sections/${sectionId}/topics/reorder`, {
      method: 'POST', body: JSON.stringify({ orderedTopicIds: ids }),
    }));
  };

  const handleMoveTopicToSection = (topicId, newSectionId) => {
    run(() => api(`/api/admin/elite-technologies/${slug}/topics/${topicId}/move`, {
      method: 'POST', body: JSON.stringify({ sectionId: Number(newSectionId) }),
    }));
  };

  // ---- Drag and drop: move a topic within or between sections -------------
  // Native HTML5 DnD, no new dependency. The grip handle is the drag source so
  // dragging never fights with the checkbox, the expand button, or the inputs
  // in the expanded panel. The arrows and the "Move to section" select below
  // remain as a keyboard/touch-accessible equivalent — drag is an addition,
  // not the only way to do this.
  const onTopicDragStart = (e, topic, sectionId) => {
    setDragTopic({ id: topic.id, sectionId });
    setDropAt(null);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(topic.id));
    } catch { /* some browsers disallow setData here; drag still works */ }
  };

  const onTopicDragEnd = () => { setDragTopic(null); setDropAt(null); };

  /** Hovering a row: top half inserts before it, bottom half after it. */
  const onTopicDragOver = (e, sectionId, index) => {
    if (!dragTopic) return;
    e.preventDefault();
    e.stopPropagation();          // keep the section handler from coarsening this
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
    const rect = e.currentTarget.getBoundingClientRect();
    const before = rect.height > 0 && (e.clientY - rect.top) < rect.height / 2;
    const idx = before ? index : index + 1;
    setDropAt((prev) => (prev && prev.sectionId === sectionId && prev.index === idx ? prev : { sectionId, index: idx }));
  };

  /** Hovering the section body (gaps, empty sections) appends to the end. */
  const onSectionDragOver = (e, section) => {
    if (!dragTopic) return;
    e.preventDefault();
    const end = (section.topics || []).length;
    setDropAt((prev) => (prev && prev.sectionId === section.id ? prev : { sectionId: section.id, index: end }));
  };

  const commitDrop = (targetSectionId) => {
    if (!dragTopic || !dropAt || dropAt.sectionId !== targetSectionId) { onTopicDragEnd(); return; }
    const target = sections.find((s) => s.id === targetSectionId);
    const ids = (target?.topics || []).map((t) => t.id);
    const fromIdx = ids.indexOf(dragTopic.id);
    // dropAt.index counts the dragged topic itself while it is still shown in
    // this section; the API's `order` is an index into the list WITHOUT it.
    let insertAt = dropAt.index;
    if (fromIdx !== -1 && fromIdx < insertAt) insertAt -= 1;
    const unchanged = dragTopic.sectionId === targetSectionId && fromIdx === insertAt;
    const topicId = dragTopic.id;
    onTopicDragEnd();
    if (unchanged) return;
    run(() => api(`/api/admin/elite-technologies/${slug}/topics/${topicId}/move`, {
      method: 'POST', body: JSON.stringify({ sectionId: targetSectionId, order: insertAt }),
    }));
  };

  const onTopicDrop = (e, section) => { e.preventDefault(); e.stopPropagation(); commitDrop(section.id); };

  /** Insertion marker, rendered only while a drag is in progress. */
  const DropLine = ({ sectionId, index }) => (
    <div
      className={`h-0.5 -my-0.5 rounded transition-colors ${
        dropAt && dropAt.sectionId === sectionId && dropAt.index === index ? 'bg-violet-500' : 'bg-transparent'
      }`}
    />
  );

  const toggleSelectTopic = (topicId) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId); else next.add(topicId);
      return next;
    });
  };

  /** Per-section "select all": checks every topic in the section, or clears
   * them all when they're already all selected (the header checkbox shows an
   * indeterminate state when only some are). */
  const toggleSelectSection = (section) => {
    const ids = (section.topics || []).map((t) => t.id);
    if (!ids.length) return;
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleMergeConfirm = () => {
    const topicIds = Array.from(selectedTopicIds);
    run(async () => {
      await api(`/api/admin/elite-technologies/${slug}/topics/merge`, {
        method: 'POST', body: JSON.stringify({ topicIds, title: mergeTitle.trim() || undefined }),
      });
      setSelectedTopicIds(new Set());
      setMergeModal(false);
      setMergeTitle('');
    });
  };

  const handleSplitConfirm = () => {
    const newTopics = splitTitles.split('\n').map((s) => s.trim()).filter(Boolean).map((title) => ({ title }));
    if (newTopics.length < 2) {
      window.alert('Provide at least 2 titles (one per line) to split into.');
      return;
    }
    run(async () => {
      await api(`/api/admin/elite-technologies/${slug}/topics/${splitTopicId}/split`, {
        method: 'POST', body: JSON.stringify({ newTopics }),
      });
      setSplitTopicId(null);
      setSplitTitles('');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] p-6 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-violet-500" />
      </div>
    );
  }

  const sections = roadmap?.sections || [];
  const qs = roadmap?.qualityScores;
  const canApprove = technology?.generation_status === 'ROADMAP_DRAFT' && roadmap?.totalTopics > 0;

  return (
    <div className="min-h-screen bg-[#F6F7FB] p-6">
      <div className="max-w-5xl mx-auto pb-16">
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back to Technology Intelligence
        </button>

        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-gray-900">{technology?.title} — Roadmap</h1>
            <p className="text-xs text-gray-700 mt-1">
              Status: <span className="font-bold">{(technology?.generation_status || '').replace(/_/g, ' ')}</span>
              {' · '}{roadmap?.totalTopics || 0} topics across {sections.length} sections
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {busy && <Loader2 size={20} className="animate-spin text-violet-500 self-center mr-1" />}
            <button
              onClick={loadVersions}
              title="Roadmap version history"
              className="px-3 py-2 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <History size={13} /> {roadmapVersion ? `v${roadmapVersion.version}` : 'History'}
            </button>
            <button
              onClick={openRegen}
              disabled={busy || isGenerating || !roadmap?.totalTopics}
              title={roadmap?.totalTopics ? 'Regenerate this roadmap from your feedback' : 'Generate a roadmap first'}
              className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              <Sparkles size={14} /> Re-generate RoadMap
            </button>
            <button
              onClick={handleApprove}
              disabled={!canApprove || busy}
              title={canApprove ? 'Approve this roadmap for content generation' : 'Only available from ROADMAP_DRAFT'}
              className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#059669,#10B981)' }}
            >
              <Check size={14} /> Approve Roadmap
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-4">{error}</div>
        )}

        {/* Regeneration in progress (the empty-roadmap card below covers first
            generation; this covers regenerating a roadmap that already exists). */}
        {isGenerating && roadmap?.totalTopics > 0 && (
          <div className="p-3 bg-violet-50 border-l-4 border-violet-500 text-violet-800 text-xs rounded mb-4 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin shrink-0" />
            <span>
              <b>Regenerating RoadMap…</b> Your current roadmap stays exactly as it is until the new one passes validation.
              This usually takes 1–3 minutes and keeps running if you leave this page.
            </span>
          </div>
        )}

        {/* The last regeneration attempt failed. The roadmap below is the one
            the admin already had — untouched, which is the point — but that is
            only reassuring if we say so. */}
        {lastRegenFailed && (
          <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded mb-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-bold text-red-800 inline-flex items-center gap-1">
                <AlertTriangle size={13} />
                {lastAttemptWasRegen ? 'Last RoadMap regeneration failed — nothing was changed' : 'Last roadmap job failed'}
              </p>
              <button onClick={() => setRegenDismissed(true)} className="text-red-400 hover:text-red-700 shrink-0" aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
            <p className="text-[11px] text-red-700 mt-1">{generationJob.error}</p>
            <p className="text-[11px] text-red-600 mt-1">
              The roadmap shown below is still your previous version — no topics were added, removed or lost.
              Adjust your feedback and try again.
            </p>
            <button
              onClick={openRegen}
              disabled={busy}
              className="mt-2 px-3 py-1.5 rounded-lg text-white text-[11px] font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              <RefreshCw size={12} /> Try again
            </button>
          </div>
        )}

        {/* Change summary — computed from the actual roadmap diff, never from
            what the model claimed it changed. Suppressed while a newer attempt
            is failing, so a stale success banner can't imply the last thing the
            admin asked for went through. */}
        {(() => {
          const diff = lastDiff || (roadmapVersion && roadmapVersion.source === 'regenerated' ? roadmapVersion.diff : null);
          if (!diff || isGenerating || lastRegenFailed) return null;
          const c = diff.counts || {};
          const rows = [
            ['+', `${c.topicsAdded || 0} topic(s) added`, 'text-emerald-700', c.topicsAdded],
            ['+', `${c.sectionsAdded || 0} section(s) added`, 'text-emerald-700', c.sectionsAdded],
            ['~', `${c.topicsExpanded || 0} topic(s) expanded`, 'text-blue-700', c.topicsExpanded],
            ['↔', `${c.topicsReorganized || 0} topic(s) reorganized`, 'text-amber-700', c.topicsReorganized],
            ['−', `${c.topicsRemoved || 0} topic(s) removed`, 'text-red-700', c.topicsRemoved],
            ['−', `${c.sectionsRemoved || 0} section(s) removed`, 'text-red-700', c.sectionsRemoved],
          ].filter(([, , , n]) => n > 0);
          return (
            <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded mb-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-bold text-emerald-900">
                  RoadMap Regenerated Successfully
                  {roadmapVersion ? ` — Version ${roadmapVersion.version - 1} → ${roadmapVersion.version}` : ''}
                  <span className="font-normal text-emerald-800"> ({c.topicsBefore} → {c.topicsAfter} topics)</span>
                </p>
                {(diff.added?.length || diff.removed?.length || diff.expanded?.length || diff.reorganized?.length) ? (
                  <button onClick={() => setShowDiffDetail((v) => !v)} className="text-[11px] font-bold text-emerald-700 hover:underline">
                    {showDiffDetail ? 'Hide changes' : 'View changes'}
                  </button>
                ) : null}
              </div>
              <div className="flex gap-3 flex-wrap mt-1">
                {rows.length === 0
                  ? <span className="text-[11px] text-emerald-800">No structural changes detected.</span>
                  : rows.map(([sign, label, tone]) => (
                    <span key={label} className={`text-[11px] font-semibold ${tone}`}>{sign} {label}</span>
                  ))}
              </div>
              {showDiffDetail && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                  {[['Added', diff.added, PlusIcon, 'text-emerald-700'],
                    ['Expanded', diff.expanded, MoveRight, 'text-blue-700'],
                    ['Reorganized', diff.reorganized, MoveRight, 'text-amber-700'],
                    ['Removed', diff.removed, Minus, 'text-red-700']]
                    .filter(([, list]) => (list || []).length > 0)
                    .map(([label, list, Icon, tone]) => (
                      <div key={label}>
                        <p className={`font-bold ${tone} mb-0.5 inline-flex items-center gap-1`}><Icon size={11} /> {label}</p>
                        <ul className="text-gray-700 space-y-0.5">
                          {list.map((item, i) => (
                            <li key={i}>
                              • {item.title}
                              {item.from && item.to ? (
                                <span className="text-gray-500"> ({item.from.section} → {item.to.section}{item.from.difficulty !== item.to.difficulty ? `, ${item.from.difficulty} → ${item.to.difficulty}` : ''})</span>
                              ) : item.section ? <span className="text-gray-600"> — {item.section}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Quality scores */}
        {roadmap?.totalTopics > 0 && qs && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-4">
            <h2 className="text-xs font-black text-gray-700 uppercase tracking-wide mb-3">Roadmap Quality (computed)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ScoreBar label="Coverage" value={qs.coverageScore} hint="% topics with a description and learning objectives" />
              <ScoreBar label="Dependency Integrity" value={qs.dependencyIntegrityScore} hint="% dependencies that point to a real topic" />
              <ScoreBar label="Difficulty Progression" value={qs.difficultyProgressionScore} hint="% consecutive topics that don't regress in difficulty" />
              <ScoreBar label="Continuity" value={qs.continuityScore} hint="% prerequisites that appear earlier in the roadmap" />
            </div>
            <p className="text-[10px] text-gray-600 mt-3">{qs.note}</p>
          </div>
        )}

        {/* Blueprint panel */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-4 overflow-hidden">
          <button
            onClick={() => setBlueprintOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4"
          >
            <span className="text-xs font-black text-gray-700 uppercase tracking-wide">Elite Mastery Blueprint</span>
            <div className="flex items-center gap-2">
              {!blueprint && (
                <span
                  onClick={(e) => { e.stopPropagation(); handleGenerateBlueprint(); }}
                  className="px-3 py-1.5 rounded-lg text-white text-[11px] font-bold shadow inline-flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  Generate Blueprint
                </span>
              )}
              {blueprintOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>
          {blueprintOpen && blueprint && (
            <div className="px-5 pb-5 text-sm text-gray-700 space-y-3 border-t border-gray-50 pt-4">
              <Field label="Foundational Concepts">{(blueprint.blueprint.foundational_concepts || []).join(', ') || '—'}</Field>
              <Field label="Intermediate Concepts">{(blueprint.blueprint.intermediate_concepts || []).join(', ') || '—'}</Field>
              <Field label="Advanced Concepts">{(blueprint.blueprint.advanced_concepts || []).join(', ') || '—'}</Field>
              <Field label="Expert Concepts">{(blueprint.blueprint.expert_concepts || []).join(', ') || '—'}</Field>
              <Field label="Competencies by Category">
                <ul className="list-disc pl-4 space-y-0.5">
                  {(blueprint.blueprint.competencies_by_category || []).map((c, i) => (
                    <li key={i}><b>{c.category}:</b> {(c.competencies || []).join(', ')}</li>
                  ))}
                </ul>
              </Field>
              <Field label="Common Misconceptions">{(blueprint.blueprint.common_misconceptions || []).join('; ') || '—'}</Field>
              <Field label="Production Scenarios">
                <ul className="list-disc pl-4 space-y-0.5">
                  {(blueprint.blueprint.production_scenarios || []).map((s, i) => <li key={i}><b>{s.title}:</b> {s.description}</li>)}
                </ul>
              </Field>
              <Field label="Design Patterns">
                <ul className="list-disc pl-4 space-y-0.5">
                  {(blueprint.blueprint.design_patterns || []).map((p, i) => <li key={i}><b>{p.name}:</b> {p.description}</li>)}
                </ul>
              </Field>
              <Field label="Deployment Strategies">{(blueprint.blueprint.deployment_strategies || []).join(', ') || '—'}</Field>
              <Field label="Visual Opportunities">{(blueprint.blueprint.visual_opportunities || []).join('; ') || '—'}</Field>
              <Field label="Hands-On Opportunities">{(blueprint.blueprint.hands_on_opportunities || []).join('; ') || '—'}</Field>
              <Field label="Assessment Strategy">{blueprint.blueprint.assessment_strategy || '—'}</Field>
              <Field label="Interview Strategy">{blueprint.blueprint.interview_strategy || '—'}</Field>
              <Field label="Learning Progression">{(blueprint.blueprint.learning_progression || []).join(' → ') || '—'}</Field>
              <Field label="Topic Dependency Notes">{blueprint.blueprint.topic_dependency_notes || '—'}</Field>
              <Field label="Mastery Evidence">{(blueprint.blueprint.mastery_evidence || []).join('; ') || '—'}</Field>
            </div>
          )}
        </div>

        {/* Roadmap generation (before any roadmap exists) */}
        {roadmap?.totalTopics === 0 && isGenerating && (
          <div className="bg-white rounded-2xl shadow-sm border border-violet-100 p-8 text-center mb-4">
            <Loader2 size={26} className="animate-spin text-violet-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-800">Generating roadmap…</p>
            <p className="text-xs text-gray-700 mt-1">
              TSG is planning sections and topics from the Blueprint. This usually takes 1–3 minutes
              {elapsedSec > 0 && <> · {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')} elapsed</>}.
            </p>
            <p className="text-[11px] text-gray-600 mt-2">
              It keeps running on the server if you leave this page — come back and it will be here.
            </p>
            {isOverdue && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-amber-700 font-semibold mb-2">
                  This is taking longer than expected. The run may have been interrupted.
                </p>
                <button
                  onClick={handleResetAndRetry}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg,#B45309,#F59E0B)' }}
                >
                  <RefreshCw size={13} /> Reset & retry
                </button>
              </div>
            )}
          </div>
        )}

        {roadmap?.totalTopics === 0 && !isGenerating && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center mb-4">
            {technology?.generation_status === 'FAILED' && generationJob?.status === 'FAILED' ? (
              <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-4 text-left">
                <span className="font-bold">Last roadmap generation failed:</span> {generationJob.error || 'Unknown error'}
              </div>
            ) : technology?.generation_status === 'FAILED' ? (
              <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-4 text-left">
                The last generation step failed. You can retry below.
              </div>
            ) : null}
            <p className="text-sm text-gray-700 mb-3">No roadmap yet for this technology.</p>
            <button
              onClick={handleGenerateRoadmap}
              disabled={!blueprint || busy}
              title={!blueprint ? 'Generate an Elite Mastery Blueprint first' : ''}
              className="px-4 py-2 rounded-lg text-white text-sm font-bold shadow inline-flex items-center gap-2 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              {technology?.generation_status === 'FAILED' ? 'Retry Roadmap Generation' : 'Generate Roadmap'}
            </button>
            {!blueprint && <p className="text-[11px] text-gray-600 mt-2">Generate the Blueprint above first.</p>}
          </div>
        )}

        {/* Sections */}
        {sections.map((section, sIdx) => {
          const sectionTopicIds = (section.topics || []).map((t) => t.id);
          const selectedInSection = sectionTopicIds.filter((id) => selectedTopicIds.has(id)).length;
          const allInSectionSelected = sectionTopicIds.length > 0 && selectedInSection === sectionTopicIds.length;
          const isDropTarget = !!dragTopic && dropAt && dropAt.sectionId === section.id;
          return (
          <div
            key={section.id}
            className={`bg-white rounded-2xl shadow-sm p-5 mb-4 border transition-colors ${isDropTarget ? 'border-violet-400 ring-2 ring-violet-100' : 'border-gray-100'}`}
          >
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              {/* Select-all for this section (mirrors the per-topic checkbox
                  below; indeterminate when only some topics are selected). */}
              <input
                type="checkbox"
                aria-label={`Select all topics in ${section.title}`}
                title={sectionTopicIds.length ? (allInSectionSelected ? 'Clear selection in this section' : 'Select all topics in this section') : 'No topics to select'}
                disabled={!sectionTopicIds.length}
                checked={allInSectionSelected}
                ref={(el) => { if (el) el.indeterminate = selectedInSection > 0 && !allInSectionSelected; }}
                onChange={() => toggleSelectSection(section)}
                className="disabled:opacity-30"
              />
              {renamingSectionId === section.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="px-2 py-1 border border-gray-200 rounded-lg text-sm font-bold flex-1"
                  />
                  <button onClick={() => handleRenameSection(section.id)} className="text-emerald-600"><Check size={16} /></button>
                  <button onClick={() => setRenamingSectionId(null)} className="text-gray-600 hover:text-gray-900"><X size={16} /></button>
                </div>
              ) : (
                <h3 className="font-black text-gray-900 text-sm flex items-center gap-2">
                  {section.title}
                  <IconAction
                    tip="Rename this section"
                    onClick={() => { setRenamingSectionId(section.id); setRenameValue(section.title); }}
                  >
                    <Pencil size={12} />
                  </IconAction>
                </h3>
              )}
              <div className="flex items-center gap-1.5">
                <IconAction
                  tip={sIdx === 0 ? 'Already the first section' : 'Move this section up'}
                  disabled={sIdx === 0 || busy}
                  onClick={() => handleMoveSection(section.id, 'up')}
                >
                  <ArrowUpCircle size={16} />
                </IconAction>
                <IconAction
                  tip={sIdx === sections.length - 1 ? 'Already the last section' : 'Move this section down'}
                  disabled={sIdx === sections.length - 1 || busy}
                  onClick={() => handleMoveSection(section.id, 'down')}
                >
                  <ArrowDownCircle size={16} />
                </IconAction>
                <IconAction
                  tip="Regenerate this section's topics with AI"
                  tone="hover:text-violet-600"
                  disabled={busy}
                  onClick={() => handleRegenerateSection(section.id)}
                >
                  <RefreshCw size={14} />
                </IconAction>
                <IconAction
                  tip="Delete this section and all its topics"
                  tone="hover:text-red-600"
                  disabled={busy}
                  onClick={() => handleRemoveSection(section)}
                >
                  <Trash2 size={14} />
                </IconAction>
              </div>
            </div>

            <div
              className={`space-y-2 ${dragTopic ? 'min-h-[2.5rem] rounded-lg' : ''}`}
              onDragOver={(e) => onSectionDragOver(e, section)}
              onDrop={(e) => onTopicDrop(e, section)}
            >
              {dragTopic && (section.topics || []).length === 0 && (
                <p className="text-[11px] text-violet-500 text-center py-2 border border-dashed border-violet-300 rounded-lg">
                  Drop here to move the topic into this section
                </p>
              )}
              {(section.topics || []).map((topic, tIdx) => {
                const expanded = expandedTopicId === topic.id;
                const isDragging = dragTopic && dragTopic.id === topic.id;
                return (
                  <React.Fragment key={topic.id}>
                    {dragTopic && <DropLine sectionId={section.id} index={tIdx} />}
                  <div
                    className={`border rounded-lg transition-opacity ${isDragging ? 'border-violet-300 opacity-40' : 'border-gray-100'}`}
                    onDragOver={(e) => onTopicDragOver(e, section.id, tIdx)}
                    onDrop={(e) => onTopicDrop(e, section)}
                  >
                    <div className="flex items-center gap-2 px-3 py-2">
                      <span className="relative inline-flex group shrink-0">
                        <span
                          draggable={!busy}
                          onDragStart={(e) => onTopicDragStart(e, topic, section.id)}
                          onDragEnd={onTopicDragEnd}
                          aria-label="Drag to move this topic"
                          className={`text-gray-700 hover:text-violet-600 transition-colors ${busy ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
                        >
                          <GripVertical size={14} />
                        </span>
                        <span
                          role="tooltip"
                          className="pointer-events-none absolute bottom-full left-0 mb-1.5 z-30 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] font-semibold text-white shadow-lg opacity-0 translate-y-0.5 transition-all duration-100 group-hover:opacity-100 group-hover:translate-y-0"
                        >
                          Drag to reorder — or into another section
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        checked={selectedTopicIds.has(topic.id)}
                        onChange={() => toggleSelectTopic(topic.id)}
                        aria-label={`Select ${topic.title}`}
                        title="Select this topic (pick two or more to merge them)"
                      />
                      <button
                        onClick={() => setExpandedTopicId(expanded ? null : topic.id)}
                        title={expanded ? 'Hide this topic’s details' : 'Show and edit this topic’s details'}
                        className="flex-1 flex items-center gap-2 text-left"
                      >
                        <ChevronRight size={14} className={`text-gray-700 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        <span className="text-sm font-semibold text-gray-900">{topic.title}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${DIFFICULTY_TONES[topic.difficulty] || DIFFICULTY_TONES.foundation}`}>
                          {topic.difficulty}
                        </span>
                        <span className="text-[10px] font-medium text-gray-600">{topic.prominence_class} · {topic.learning_mode}</span>
                      </button>
                      <IconAction tip="Move this topic up" disabled={busy} onClick={() => handleMoveTopic(section.id, topic.id, 'up')}>
                        <ArrowUpCircle size={14} />
                      </IconAction>
                      <IconAction tip="Move this topic down" disabled={busy} onClick={() => handleMoveTopic(section.id, topic.id, 'down')}>
                        <ArrowDownCircle size={14} />
                      </IconAction>
                      <IconAction tip="Regenerate this topic with AI" tone="hover:text-violet-600" disabled={busy} onClick={() => handleRegenerateTopic(topic.id)}>
                        <RefreshCw size={13} />
                      </IconAction>
                      <IconAction tip="Split this topic into several" tone="hover:text-amber-600" disabled={busy} onClick={() => { setSplitTopicId(topic.id); setSplitTitles(''); }}>
                        <Scissors size={13} />
                      </IconAction>
                      <IconAction tip="Delete this topic" tone="hover:text-red-600" disabled={busy} onClick={() => handleRemoveTopic(topic)}>
                        <Trash2 size={13} />
                      </IconAction>
                    </div>

                    {expanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-50 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                        <Field label="Title">
                          <input
                            defaultValue={topic.title}
                            onBlur={(e) => e.target.value.trim() && e.target.value !== topic.title && patchTopic(topic.id, { title: e.target.value.trim() })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          />
                        </Field>
                        <Field label="Move to section">
                          <select
                            value={section.id}
                            onChange={(e) => handleMoveTopicToSection(topic.id, e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          >
                            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                          </select>
                        </Field>
                        <div className="md:col-span-2">
                          <Field label="Description">
                            <textarea
                              rows={2}
                              defaultValue={topic.description || ''}
                              onBlur={(e) => e.target.value !== (topic.description || '') && patchTopic(topic.id, { description: e.target.value })}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                            />
                          </Field>
                        </div>
                        <Field label="Difficulty">
                          <select
                            defaultValue={topic.difficulty}
                            onChange={(e) => patchTopic(topic.id, { difficulty: e.target.value })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          >
                            {DIFFICULTY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </Field>
                        <Field label="Learning Mode">
                          <select
                            defaultValue={topic.learning_mode}
                            onChange={(e) => patchTopic(topic.id, { learning_mode: e.target.value })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          >
                            {LEARNING_MODE_OPTIONS.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                          </select>
                        </Field>
                        <Field label="Prominence Score">
                          <input
                            type="number" min={0} max={100} defaultValue={topic.prominence_score}
                            onBlur={(e) => patchTopic(topic.id, { prominence_score: Number(e.target.value) })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          />
                        </Field>
                        <Field label="Interview Relevance">
                          <input
                            type="number" min={0} max={100} defaultValue={topic.interview_relevance_score}
                            onBlur={(e) => patchTopic(topic.id, { interview_relevance_score: Number(e.target.value) })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          />
                        </Field>
                        <Field label="Production Relevance">
                          <input
                            type="number" min={0} max={100} defaultValue={topic.production_relevance_score}
                            onBlur={(e) => patchTopic(topic.id, { production_relevance_score: Number(e.target.value) })}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                          />
                        </Field>
                        <Field label="Prerequisites (comma-separated topic titles)">
                          <TagListInput value={topic.prerequisites} onChange={(arr) => patchTopic(topic.id, { prerequisites: arr })} placeholder="e.g. What is a table format" />
                        </Field>
                        <Field label="Dependencies (comma-separated topic titles)">
                          <TagListInput value={topic.dependencies} onChange={(arr) => patchTopic(topic.id, { dependencies: arr })} />
                        </Field>
                        <Field label="Competencies">
                          <TagListInput value={topic.competencies} onChange={(arr) => patchTopic(topic.id, { competencies: arr })} />
                        </Field>
                        <Field label="Learning Objectives">
                          <TagListInput value={topic.learning_objectives} onChange={(arr) => patchTopic(topic.id, { learning_objectives: arr })} />
                        </Field>
                        <div className="md:col-span-2">
                          <Field label="Visual Opportunity Notes">
                            <textarea
                              rows={2}
                              defaultValue={topic.visual_opportunity_notes || ''}
                              onBlur={(e) => e.target.value !== (topic.visual_opportunity_notes || '') && patchTopic(topic.id, { visual_opportunity_notes: e.target.value || null })}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                            />
                          </Field>
                        </div>
                        <div className="md:col-span-2">
                          <Field label="Assessment Opportunity Notes">
                            <textarea
                              rows={2}
                              defaultValue={topic.assessment_opportunity_notes || ''}
                              onBlur={(e) => e.target.value !== (topic.assessment_opportunity_notes || '') && patchTopic(topic.id, { assessment_opportunity_notes: e.target.value || null })}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                            />
                          </Field>
                        </div>
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}
              {dragTopic && <DropLine sectionId={section.id} index={(section.topics || []).length} />}
            </div>

            {addingTopicFor === section.id ? (
              <div className="flex items-center gap-2 mt-3">
                <input
                  autoFocus
                  value={addingTopicTitle}
                  onChange={(e) => setAddingTopicTitle(e.target.value)}
                  placeholder="New topic title"
                  className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                />
                <button onClick={() => handleAddTopic(section.id)} className="text-emerald-600"><Check size={16} /></button>
                <button onClick={() => setAddingTopicFor(null)} className="text-gray-600 hover:text-gray-900"><X size={16} /></button>
              </div>
            ) : (
              <button
                onClick={() => { setAddingTopicFor(section.id); setAddingTopicTitle(''); }}
                className="mt-3 text-xs font-bold text-violet-600 hover:text-violet-800 inline-flex items-center gap-1"
              >
                <Plus size={13} /> Add Topic
              </button>
            )}
          </div>
          );
        })}

        {sections.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
            <div className="flex items-center gap-2">
              <input
                value={addingSectionTitle}
                onChange={(e) => setAddingSectionTitle(e.target.value)}
                placeholder="New section title"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <button
                onClick={handleAddSection}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
              >
                <Plus size={14} /> Add Section
              </button>
            </div>
          </div>
        )}

        {/* Merge bar */}
        {selectedTopicIds.size >= 1 && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-3 z-20">
            <span className="text-xs font-bold">{selectedTopicIds.size} topic(s) selected</span>
            <button
              disabled={selectedTopicIds.size < 2}
              onClick={() => setMergeModal(true)}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-violet-600 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <GitMerge size={13} /> Merge
            </button>
            <button onClick={() => setSelectedTopicIds(new Set())} className="text-xs text-gray-300 hover:text-white">Clear</button>
          </div>
        )}

        {/* Merge modal */}
        {mergeModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
              <h3 className="font-black text-gray-800 mb-3">Merge {selectedTopicIds.size} Topics</h3>
              <p className="text-xs text-gray-500 mb-3">
                Prerequisites, learning objectives, dependencies, and competencies are unioned automatically.
                Give the merged topic a title (optional — defaults to the first topic's).
              </p>
              <input
                value={mergeTitle}
                onChange={(e) => setMergeTitle(e.target.value)}
                placeholder="Merged topic title (optional)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleMergeConfirm}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  Merge
                </button>
                <button onClick={() => setMergeModal(false)} className="flex-1 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Split modal */}
        {splitTopicId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
              <h3 className="font-black text-gray-800 mb-3">Split Topic</h3>
              <p className="text-xs text-gray-500 mb-3">
                One title per line — at least 2. No AI is used here; you provide the titles.
              </p>
              <textarea
                rows={5}
                value={splitTitles}
                onChange={(e) => setSplitTitles(e.target.value)}
                placeholder={'Topic A title\nTopic B title'}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleSplitConfirm}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  Split
                </button>
                <button onClick={() => setSplitTopicId(null)} className="flex-1 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Re-generate RoadMap (26-Aug-2026) */}
      {regenOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog" aria-modal="true" aria-labelledby="regen-title"
          onKeyDown={(e) => { if (e.key === 'Escape') closeRegen(); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-start justify-between mb-1">
                <h2 id="regen-title" className="text-lg font-black text-gray-900 inline-flex items-center gap-2">
                  <Sparkles size={18} className="text-violet-600" /> Re-generate RoadMap
                </h2>
                <button onClick={closeRegen} disabled={regenBusy} className="text-gray-600 hover:text-gray-900 disabled:opacity-40" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <p className="text-xs text-gray-700 mb-4">
                Tell us what is missing, incorrect, or needs to be improved. The roadmap will be regenerated using your
                feedback, with the current roadmap as the starting point.
              </p>

              {regenError && (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-3">
                  <p className="font-bold mb-0.5 inline-flex items-center gap-1"><AlertTriangle size={12} /> RoadMap regeneration failed</p>
                  {regenError}
                  <p className="mt-1 text-red-600">Your current roadmap has not been changed. You can adjust the feedback and try again.</p>
                </div>
              )}

              <label htmlFor="regen-feedback" className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-1">
                What should be changed?
              </label>
              <textarea
                id="regen-feedback"
                autoFocus
                rows={5}
                value={regenFeedback}
                onChange={(e) => setRegenFeedback(e.target.value)}
                disabled={regenBusy}
                maxLength={6000}
                placeholder="Example: Add Kubernetes Operators, StatefulSets, CRDs and Pod Disruption Budgets, and deeper troubleshooting. The networking section is too shallow. Add a dedicated production troubleshooting section and include service mesh concepts such as Istio and Envoy."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs mb-1 disabled:bg-gray-50"
              />
              <p className="text-[10px] text-gray-600 mb-4">{regenFeedback.length}/6000</p>

              <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-2">Optional guidance</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[
                  ['missingSections', 'Missing Sections', 'Production Troubleshooting'],
                  ['missingTopics', 'Missing Topics', 'StatefulSets, Operators, CRDs'],
                  ['topicsToExpand', 'Topics to Expand', 'Kubernetes Networking'],
                  ['topicsToRemove', 'Topics to Remove', ''],
                  ['topicsToReorganize', 'Topics to Reorganize', 'Service Mesh → Advanced Networking'],
                ].map(([key, label, ph]) => (
                  <div key={key}>
                    <label htmlFor={`regen-${key}`} className="block text-[10px] font-bold text-gray-600 uppercase tracking-wide mb-0.5">{label}</label>
                    <input
                      id={`regen-${key}`}
                      value={regenFields[key]}
                      onChange={(e) => setRegenFields((p) => ({ ...p, [key]: e.target.value }))}
                      disabled={regenBusy}
                      placeholder={ph}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs disabled:bg-gray-50"
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-600 mb-4">Separate multiple entries with commas. All optional — the box above is enough on its own.</p>

              <fieldset className="mb-4" disabled={regenBusy}>
                <legend className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-1">Regeneration behavior</legend>
                {[
                  ['improve', 'Improve existing roadmap', 'Smallest set of changes that satisfies your feedback. Everything else is left alone.'],
                  ['rebuild', 'Rebuild using current roadmap + feedback', 'Allows restructuring sections, still using the current roadmap as source material.'],
                ].map(([value, label, hint]) => (
                  <label key={value} className="flex items-start gap-2 mb-1.5 cursor-pointer">
                    <input type="radio" name="regenMode" value={value} checked={regenMode === value}
                      onChange={() => setRegenMode(value)} className="mt-0.5" />
                    <span>
                      <span className="text-xs font-semibold text-gray-800">{label}</span>
                      <span className="block text-[10px] text-gray-600">{hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="flex gap-2">
                <button onClick={closeRegen} disabled={regenBusy}
                  className="flex-1 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm disabled:opacity-40">
                  Cancel
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={regenBusy || !regenHasInput}
                  title={regenHasInput ? '' : 'Describe what should change first'}
                  className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm shadow inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  {regenBusy ? <><Loader2 size={15} className="animate-spin" /> Regenerating RoadMap…</> : <><Sparkles size={15} /> Re-generate RoadMap</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version history */}
      {showVersions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-lg font-black text-gray-900 inline-flex items-center gap-2"><History size={18} /> RoadMap versions</h2>
                <button onClick={() => setShowVersions(false)} className="text-gray-600 hover:text-gray-900" aria-label="Close"><X size={18} /></button>
              </div>
              {/* Attempts that failed never become versions — but leaving them
                  out entirely is what made a rejected regeneration look like
                  nothing had happened at all. */}
              {failedAttempts.length > 0 && (
                <div className="mb-4">
                  <p className="text-[11px] font-bold text-red-700 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1">
                    <AlertTriangle size={12} /> Failed attempts (no version created, roadmap unchanged)
                  </p>
                  <div className="space-y-2">
                    {failedAttempts.map((a) => (
                      <div key={a.id} className="border border-red-100 bg-red-50/50 rounded-lg p-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">FAILED</span>
                          {a.payload?.mode && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{a.payload.mode}</span>
                          )}
                          <span className="text-[11px] text-gray-600 ml-auto">
                            {a.created_at ? new Date(a.created_at).toLocaleString() : ''}{a.requested_by ? ` · ${a.requested_by}` : ''}
                          </span>
                        </div>
                        <p className="text-[11px] text-red-700 mt-1">{a.error}</p>
                        {a.payload?.feedback && (
                          <p className="text-[11px] text-gray-600 mt-1 italic border-l-2 border-gray-200 pl-2 whitespace-pre-wrap line-clamp-3">
                            {a.payload.feedback}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {versions.length === 0 && failedAttempts.length === 0 ? (
                <p className="text-xs text-gray-600 py-6 text-center">No version history recorded yet.</p>
              ) : versions.length === 0 ? null : (
                <div className="space-y-3">
                  {versions.map((v) => (
                    <div key={v.id} className={`border rounded-lg p-3 ${v.status === 'ACTIVE' ? 'border-violet-300 bg-violet-50/40' : 'border-gray-100'}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-black text-gray-900">Version {v.version}</span>
                        {v.status === 'ACTIVE' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">CURRENT</span>}
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{v.source}</span>
                        <span className="text-[11px] text-gray-500">{v.section_count} sections · {v.topic_count} topics</span>
                        <span className="text-[11px] text-gray-600 ml-auto">{v.created_at ? new Date(v.created_at).toLocaleString() : ''}{v.created_by ? ` · ${v.created_by}` : ''}</span>
                      </div>
                      {v.diff?.counts && (
                        <p className="text-[11px] text-gray-600 mt-1">
                          +{v.diff.counts.topicsAdded} added · ~{v.diff.counts.topicsExpanded} expanded · ↔{v.diff.counts.topicsReorganized} reorganized · −{v.diff.counts.topicsRemoved} removed
                        </p>
                      )}
                      {v.feedback && (
                        <p className="text-[11px] text-gray-700 mt-1 italic border-l-2 border-gray-200 pl-2 whitespace-pre-wrap">{v.feedback}</p>
                      )}
                      {v.validation?.warnings?.length > 0 && (
                        <p className="text-[10px] text-amber-700 mt-1">{v.validation.warnings.length} validation warning(s)</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EliteRoadmapEditor;
